/**
 * Selección del modal de impacto.
 *
 * La unidad es la **novedad** —un par DNI + concepto—, no el colaborador ni el
 * concepto: a alguien se le puede impactar una parte de lo suyo y dejar el
 * resto para revisar. Las casillas de colaborador y de "seleccionar todo" solo
 * agregan o quitan grupos de esas claves.
 *
 * Vive aparte de la vista a propósito: es la lógica que decide qué se liquida,
 * y tiene que poder leerse y probarse sin montar un modal.
 */

import type { NovedadConsolidada, ResumenConcepto } from '../../api';

export type EstadoCasilla = 'si' | 'no' | 'parcial';

/** La clave de una novedad. Ni el DNI ni el concepto llevan "|". */
export const claveNovedad = (n: Pick<NovedadConsolidada, 'dni' | 'concepto'>): string =>
  `${n.dni}|${n.concepto}`;

/** El concepto de una clave, para contar cuántos distintos hay elegidos. */
export const conceptoDeClave = (clave: string): string => clave.split('|')[1] ?? '';

export interface LineaConcepto {
  readonly clave: string;
  readonly concepto: string;
  readonly descripcion: string;
  readonly valor: number;
  readonly unidad: string;
  readonly fechaDesde: string | null;
  readonly fechaHasta: string | null;
}

export interface GrupoColaborador {
  readonly dni: string;
  readonly nombre: string;
  readonly legajo: string;
  /** "Propio" o "Eventual": el modal mezcla las dos poblaciones. */
  readonly tipoChofer: string;
  readonly lineas: readonly LineaConcepto[];
}

/**
 * Agrupa las novedades por colaborador, en el orden en que vienen del back
 * —que ya las ordena—. La descripción sale del resumen de conceptos: la
 * novedad trae el código pelado, y "01178" no le dice nada a quien revisa.
 */
export function agruparPorColaborador(
  novedades: readonly NovedadConsolidada[],
  conceptos: readonly ResumenConcepto[],
): readonly GrupoColaborador[] {
  const descripcion = new Map(conceptos.map((c) => [c.concepto, c.descripcion]));
  const grupos = new Map<
    string,
    { dni: string; nombre: string; legajo: string; tipoChofer: string; lineas: LineaConcepto[] }
  >();

  for (const n of novedades) {
    let grupo = grupos.get(n.dni);
    if (!grupo) {
      grupo = {
        dni: n.dni,
        nombre: n.nombre,
        legajo: n.legajo,
        tipoChofer: n.tipoChofer,
        lineas: [],
      };
      grupos.set(n.dni, grupo);
    }
    grupo.lineas.push({
      clave: claveNovedad(n),
      concepto: n.concepto,
      descripcion: descripcion.get(n.concepto) ?? '',
      valor: n.valor,
      unidad: n.unidad,
      fechaDesde: n.fechaDesde,
      fechaHasta: n.fechaHasta,
    });
  }
  return [...grupos.values()];
}

/** Estado de una casilla que agrupa varias claves. */
export function estadoDe(
  claves: readonly string[],
  elegidas: ReadonlySet<string>,
): EstadoCasilla {
  const cuantas = claves.filter((c) => elegidas.has(c)).length;
  if (cuantas === 0) return 'no';
  return cuantas === claves.length ? 'si' : 'parcial';
}

/**
 * Clic en una casilla agrupadora: completa el grupo salvo que ya estuviera
 * completo, que lo vacía.
 *
 * Desde "parcial" **completa**, no vacía: es lo que hacen Gmail y el
 * explorador, y lo contrario sorprende a quien acaba de destildar uno.
 */
export function alternarGrupo(
  claves: readonly string[],
  elegidas: ReadonlySet<string>,
): ReadonlySet<string> {
  const proximas = new Set(elegidas);
  if (estadoDe(claves, elegidas) === 'si') {
    for (const clave of claves) proximas.delete(clave);
  } else {
    for (const clave of claves) proximas.add(clave);
  }
  return proximas;
}

export function alternarUna(clave: string, elegidas: ReadonlySet<string>): ReadonlySet<string> {
  const proximas = new Set(elegidas);
  if (!proximas.delete(clave)) proximas.add(clave);
  return proximas;
}

const normalizar = (texto: string): string =>
  texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Filtro del buscador: DNI, legajo o nombre, sin mayúsculas ni acentos. */
export function coincide(grupo: GrupoColaborador, texto: string): boolean {
  const buscado = normalizar(texto.trim());
  if (buscado === '') return true;
  return (
    grupo.dni.includes(buscado) ||
    grupo.legajo.includes(buscado) ||
    normalizar(grupo.nombre).includes(buscado)
  );
}

/**
 * "01/09 – 30/09/2026". Sin fechas, un guion: no se inventa un período.
 *
 * El año no se repite cuando es el mismo, que es el caso normal: dos fechas
 * completas en una celda angosta se leen peor y no agregan nada.
 */
export function rangoLegible(desde: string, hasta: string): string {
  if (desde === '—' || hasta === '—') return '—';
  return desde.slice(-4) === hasta.slice(-4) ? `${desde.slice(0, 5)} – ${hasta}` : `${desde} – ${hasta}`;
}

/**
 * Un DNI sirve para liquidar si es un documento y no un resto de carga. Misma
 * regla que el back, que la vuelve a aplicar: esto solo evita mandar algo que
 * ya se sabe que va a rebotar.
 */
export const esDniUtilizable = (dni: string): boolean =>
  /^\d{6,10}$/.test(dni.trim()) && Number(dni) > 0;
